import React from 'react';
import styled from 'styled-components';
import { DocumentVaultFilterContext } from '@/contexts/DocumentVaultFilterContext';
import Close from '@assets/icons/svgs/Close';

const SEARCH_DEBOUNCE_MS = 300;

const Wrapper = styled.div`
  position: relative;
  flex: 1;
  max-width: 420px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 32px 8px 12px;

  color: ${({ theme }) => theme.themeColors.text};
  background: ${({ theme }) => theme.themeColors.appBackground};
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 4px;

  &::placeholder {
    color: ${({ theme }) => theme.themeColors.secondaryText};
  }
`;

// Same icon and size as the filter chips, so every clear affordance in the vault matches.
const ClearButton = styled.span`
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);

  display: flex;
  align-items: center;
  cursor: pointer;

  svg {
    height: 8px;
    width: 12px;
  }

  path {
    fill: ${({ theme }) => theme.themeColors.secondaryText};
  }
`;

export const VaultSearchInput = () => {
  const { filters, setSearch } = React.useContext(DocumentVaultFilterContext);
  const [value, setValue] = React.useState(filters.search);
  const committedRef = React.useRef(filters.search);

  // Keeps the box in step when the search is cleared from elsewhere (Clear all, a chip), without
  // fighting the user's typing.
  React.useEffect(() => {
    if (filters.search !== committedRef.current) {
      committedRef.current = filters.search;
      setValue(filters.search);
    }
  }, [filters.search]);

  React.useEffect(() => {
    if (value === committedRef.current) return undefined;

    const timeout = setTimeout(() => {
      committedRef.current = value;
      setSearch(value);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [value, setSearch]);

  const handleClear = () => {
    setValue('');
    committedRef.current = '';
    setSearch('');
  };

  return (
    <Wrapper>
      <Input
        type="text"
        value={value}
        placeholder="Search filename"
        aria-label="Search filename"
        onChange={(e) => setValue(e.target.value)}
      />
      {value ? (
        <ClearButton onClick={handleClear} aria-label="Clear search">
          <Close width={12} />
        </ClearButton>
      ) : null}
    </Wrapper>
  );
};
